import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import Modal from '../../components/common/Modal.jsx';

/**
 * Modal "Editar Articulos Contenedor {n}" — espejo del `modalEditArticulos` de
 * `ListadoContenedores.jsx` de V1 (botón carrito `fa-cart-plus` por fila): muestra las
 * partidas existentes (editables) + un bloque de partidas NUEVAS con botones -/+,
 * total de piezas/importe en el encabezado y al pie, y "Guardar" que (igual que V1)
 * hace PATCH de totales al Contenedor, POST de las partidas nuevas
 * (articulo y cantidad > 0) y PATCH de las existentes.
 */
const uuid = () => crypto.randomUUID();
const moneda = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const nuevaFila = (base = {}) => ({
  id: uuid(), articulos: base.articulos || '', descripcion: '', cantidad: 0, precio: base.precio || 0, total: 0,
});
const inputCls = 'input-base w-full';

export default function ArticulosContenedorModal({ contenedor, articulosCatalogo, onClose, onGuardado }) {
  const [existentes, setExistentes] = useState([]);
  const [nuevos, setNuevos] = useState([nuevaFila()]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    apiClient.get(`/articulos-contenedor/contenedor/${contenedor._id}`)
      .then(({ data }) => {
        setExistentes((data.data || []).map((a) => ({
          id: a._id,
          articulos: a.articulos?._id || a.articulos?.[0]?._id || '',
          descripcion: a.descripcion ?? '',
          cantidad: a.cantidad ?? 0,
          precio: a.precio ?? 0,
          total: a.total ?? 0,
        })));
      })
      .catch(() => setExistentes([]));
  }, [contenedor._id]);

  const totales = useMemo(() => {
    const todas = [...existentes, ...nuevos];
    return {
      cantidad: todas.reduce((t, l) => t + (parseFloat(l.cantidad) || 0), 0),
      importe: todas.reduce((t, l) => t + (parseFloat(l.total) || 0), 0),
    };
  }, [existentes, nuevos]);

  const cambiar = (setter) => (rowId, campo, valor) => setter((prev) => prev.map((l) => {
    if (l.id !== rowId) return l;
    const nueva = { ...l, [campo]: valor };
    nueva.total = (parseFloat(nueva.cantidad) || 0) * (parseFloat(nueva.precio) || 0);
    return nueva;
  }));
  const cambiarExistente = cambiar(setExistentes);
  const cambiarNuevo = cambiar(setNuevos);

  function agregar() {
    setNuevos((prev) => [...prev, nuevaFila(prev[prev.length - 1])]);
  }
  function quitar(rowId) {
    setNuevos((prev) => (prev.length > 1 ? prev.filter((l) => l.id !== rowId) : prev));
  }

  async function guardar() {
    setGuardando(true);
    try {
      await apiClient.patch(`/contenedores/${contenedor._id}`, {
        totalGeneral: totales.importe,
        totalCantidad: totales.cantidad,
      });
      const clienteId = Array.isArray(contenedor.clientes) ? contenedor.clientes[0]?._id : contenedor.clientes;
      const navieraId = Array.isArray(contenedor.navieras) ? contenedor.navieras[0]?._id : contenedor.navieras;
      for (const l of nuevos) {
        if (l.articulos && l.cantidad > 0) {
          await apiClient.post('/articulos-contenedor', {
            fecha: contenedor.fecha,
            contenedores: contenedor._id,
            clientes: clienteId,
            navieras: navieraId,
            articulos: l.articulos,
            descripcion: l.descripcion || 'NA',
            cantidad: parseFloat(l.cantidad),
            precio: parseFloat(l.precio),
            total: parseFloat(l.total),
          });
        }
      }
      for (const l of existentes) {
        await apiClient.patch(`/articulos-contenedor/${l.id}`, {
          articulos: l.articulos,
          descripcion: l.descripcion || 'NA',
          cantidad: parseFloat(l.cantidad),
          precio: parseFloat(l.precio),
          total: parseFloat(l.total),
        });
      }
      Swal.fire({ icon: 'success', title: 'Good job!', text: 'Creado con exito', timer: 1500, showConfirmButton: false });
      onGuardado?.();
      onClose();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: error.message });
    } finally {
      setGuardando(false);
    }
  }

  const opciones = (
    <>
      <option value="">Selecciona</option>
      {articulosCatalogo.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      width="max-w-5xl"
      title={
        <span>
          Editar Articulos Contenedor {contenedor.idContenedor}
          <span className="block text-sm font-semibold text-slate-600">
            TOTAL {totales.cantidad} pzas. / {moneda(totales.importe)}
          </span>
        </span>
      }
      footer={
        <button type="button" className="btn-primary" disabled={guardando} onClick={guardar}>
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      }
    >
      <div className="grid grid-cols-12 gap-2 text-sm font-medium text-slate-600 mb-1">
        <div className="col-span-3">Articulos</div>
        <div className="col-span-3">Descripcion</div>
        <div className="col-span-2">Cantidad</div>
        <div className="col-span-2">Precio</div>
        <div className="col-span-2">Total</div>
      </div>

      {existentes.map((l) => (
        <div key={l.id} className="grid grid-cols-12 gap-2 mb-2">
          <div className="col-span-3">
            <select className="select-base w-full" value={l.articulos} onChange={(e) => cambiarExistente(l.id, 'articulos', e.target.value)}>{opciones}</select>
          </div>
          <div className="col-span-3">
            <input className={inputCls} placeholder="descripcion" value={l.descripcion} onChange={(e) => cambiarExistente(l.id, 'descripcion', e.target.value)} />
          </div>
          <div className="col-span-2">
            <input type="number" min="0" step="any" className={inputCls} placeholder="Cantidad" value={l.cantidad} onChange={(e) => cambiarExistente(l.id, 'cantidad', e.target.value)} />
          </div>
          <div className="col-span-2">
            <input type="number" min="0" step="any" className={inputCls} placeholder="Precio" value={l.precio} onChange={(e) => cambiarExistente(l.id, 'precio', e.target.value)} />
          </div>
          <div className="col-span-2">
            <input type="number" className={inputCls} placeholder="Total" value={l.total} disabled />
          </div>
        </div>
      ))}

      {/* Agregar mas articulos */}
      {nuevos.map((l) => (
        <div key={l.id} className="grid grid-cols-12 gap-2 mb-2">
          <div className="col-span-3">
            <select className="select-base w-full" value={l.articulos} onChange={(e) => cambiarNuevo(l.id, 'articulos', e.target.value)}>{opciones}</select>
          </div>
          <div className="col-span-3">
            <input className={inputCls} placeholder="descripcion" value={l.descripcion} onChange={(e) => cambiarNuevo(l.id, 'descripcion', e.target.value)} />
          </div>
          <div className="col-span-2">
            <input type="number" min="0" step="any" className={inputCls} placeholder="Cantidad" value={l.cantidad} onChange={(e) => cambiarNuevo(l.id, 'cantidad', e.target.value)} />
          </div>
          <div className="col-span-2">
            <input type="number" min="0" step="any" className={inputCls} placeholder="Precio" value={l.precio} onChange={(e) => cambiarNuevo(l.id, 'precio', e.target.value)} />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            <input type="number" className={inputCls} placeholder="Total" value={l.total} disabled />
            <button
              type="button" tabIndex={-1} title="Quitar" aria-label="Quitar"
              disabled={nuevos.length === 1} onClick={() => quitar(l.id)}
              className="p-1.5 rounded-md bg-danger-500 text-white disabled:opacity-40"
            >
              <Minus size={14} />
            </button>
            <button
              type="button" tabIndex={-1} title="Agregar" aria-label="Agregar" onClick={agregar}
              className="p-1.5 rounded-md bg-primary-600 text-white"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      ))}

      <h4 className="mt-3 font-semibold text-slate-700">
        TOTAL {totales.cantidad} pzas. / {moneda(totales.importe)}
      </h4>
    </Modal>
  );
}
